import { useState, useEffect, useCallback } from 'react'
import { getYearStartFromDate } from '../../utils/dateUtils.js'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label}</p>
        <div className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm" 
              style={{ backgroundColor: data.color }}
            />
            <span className="text-muted-foreground">Total Revenue</span>
          </div>
          <span className="font-medium">£{data.value?.toLocaleString() || '0'}</span>
        </div>
        <div className="flex items-center justify-between gap-4 text-xs mt-1">
          <span className="text-muted-foreground ml-5">Rev / Week</span>
          <span className="font-medium">£{data.payload?.revenuePerWeek?.toFixed(2) || '0.00'}</span>
        </div>
      </div>
    )
  }
  return null
}

export default function YearlyRevenueChart({ compact = false, salesData = [], loading: externalLoading = false }) {
  const [data, setData] = useState([])

  const processYearlyRevenueData = useCallback((salesData) => {
    // Group by year and calculate revenue per week
    const yearlyData = {}
    
    salesData.forEach(monthData => {
      const [y, m] = (monthData.isoMonth || monthData.month).split('-').map(Number)
      const year = y
      
      if (!yearlyData[year]) {
        yearlyData[year] = {
          totalRevenue: 0,
          weekCount: 0
        }
      }
      yearlyData[year].totalRevenue += monthData.value || 0
    })

    // Calculate weeks per year
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentWeek = getWeekOfYear(now)
    
    Object.keys(yearlyData).forEach(year => {
      const yearNum = parseInt(year)
      if (yearNum === currentYear) {
        yearlyData[year].weekCount = currentWeek
      } else {
        yearlyData[year].weekCount = 52
      }
    })

    // Ensure all years from 2021-2025 are included
    const minYear = 2021
    const maxYear = 2025
    
    for (let year = minYear; year <= maxYear; year++) {
      if (!yearlyData[year]) {
        yearlyData[year] = {
          totalRevenue: 0,
          weekCount: year === currentYear ? currentWeek : 52
        }
      }
    }

    // Convert to chart format
    return Object.entries(yearlyData).map(([year, data]) => ({
      year: year,
      revenuePerWeek: data.weekCount > 0 ? Math.round((data.totalRevenue / data.weekCount) * 100) / 100 : 0,
      totalRevenue: Math.round(data.totalRevenue * 100) / 100
    })).sort((a, b) => parseInt(a.year) - parseInt(b.year))
  }, [])

  useEffect(() => {
    if (salesData && salesData.length > 0) {
      // Process the shared sales data to create yearly revenue data
      const yearlyData = processYearlyRevenueData(salesData)
      setData(yearlyData)
    }
  }, [salesData, processYearlyRevenueData])

  // Helper function to get week number of the year
  const getWeekOfYear = (date) => {
    const start = getYearStartFromDate(date)
    const diff = date - start + (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000
    const oneWeek = 1000 * 60 * 60 * 24 * 7
    return Math.ceil(diff / oneWeek)
  }

  if (externalLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        <div className="reports-loading">
          Loading yearly revenue data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        <div className="reports-loading">
          No yearly revenue data available
        </div>
      </div>
    )
  }

  // Define colors for each year - matching RevenuePerWeekChart
  const getBarColor = (year) => {
    const colors = {
      2021: '#3366FF',  // Blue
      2022: '#FF4C4C',  // Red
      2023: '#FF8C00',  // Orange
      2024: '#009689',  // Green
      2025: '#6366F1',  // Purple
      2026: '#3366FF',  // Blue
    }
    return colors[year] || '#3366FF'
  }

  // Add color to each data item
  const dataWithColors = data.map(item => ({
    ...item,
    fill: getBarColor(item.year)
  }))

  return (
    <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dataWithColors}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis 
            dataKey="year" 
            axisLine={false}
            tickLine={false}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{ 
              value: '£ / Yr', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
            tickFormatter={(value) => `£${Math.round(value)}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="totalRevenue" 
            radius={[4, 4, 0, 0]}
            opacity={0.9}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}